// zr-core/presenceDirectory — the Online Presence Directory model: the real-time list of peers in
// a room and their live status, so a UI can "call one tap away". PURE (no I/O): the orchestrator
// feeds it presence heartbeats + per-peer signals and renders from its snapshots. Same model powers
// web and native, and Connectra Basic (peers) and Connectra+ (operators/clients with availability).

import type { PeerRole, AvailabilityStatus } from './product';

export type OnlineStatus = 'online' | 'offline';
export type E2eeStatus = 'active' | 'inactive' | 'unknown';
export type TurnsStatus = 'online' | 'unreachable' | 'offline' | 'unknown';

/** One row in the directory. `online_status`/`e2ee_status`/`turns_status` are the headline fields. */
export interface DirectoryPeer {
  peerId: string;
  online_status: OnlineStatus;
  e2ee_status: E2eeStatus;
  turns_status: TurnsStatus;
  /** Connectra+ only. */
  peerRole?: PeerRole;
  /** Connectra+ operators/admins — active | busy | offline. */
  availability_status?: AvailabilityStatus;
  roomId?: string;
  displayName?: string;
  /** Epoch ms of the last signal from/about this peer. */
  updatedAt: number;
}

/** A partial update merged onto a directory row. */
export type DirectoryPeerUpdate = Partial<Omit<DirectoryPeer, 'peerId' | 'updatedAt'>> & { peerId: string };

/**
 * Live directory of peers keyed by id. Merges partial updates (a presence poll sets online + role +
 * availability; a peer-heartbeat sets turns_status; the identity/e2ee layer sets e2ee_status), prunes
 * peers that go silent past a TTL, and returns an ordered snapshot for rendering.
 */
export class PresenceDirectory {
  private peers = new Map<string, DirectoryPeer>();

  constructor(private ttlMs = 20_000) {}

  /** Merge a partial update for a peer; returns the ordered snapshot. */
  upsert(update: DirectoryPeerUpdate, now: number): DirectoryPeer[] {
    const prev = this.peers.get(update.peerId);
    const next: DirectoryPeer = {
      peerId: update.peerId,
      online_status: update.online_status ?? prev?.online_status ?? 'online',
      e2ee_status: update.e2ee_status ?? prev?.e2ee_status ?? 'unknown',
      turns_status: update.turns_status ?? prev?.turns_status ?? 'unknown',
      peerRole: update.peerRole ?? prev?.peerRole,
      availability_status: update.availability_status ?? prev?.availability_status,
      roomId: update.roomId ?? prev?.roomId,
      displayName: update.displayName ?? prev?.displayName,
      updatedAt: now,
    };
    this.peers.set(update.peerId, next);
    return this.snapshot();
  }

  /** Mark a peer offline (kept briefly so the UI can show "went offline") — returns the snapshot. */
  markOffline(peerId: string, now: number): DirectoryPeer[] {
    const p = this.peers.get(peerId);
    if (p) this.peers.set(peerId, { ...p, online_status: 'offline', availability_status: p.availability_status ? 'offline' : undefined, updatedAt: now });
    return this.snapshot();
  }

  remove(peerId: string): DirectoryPeer[] {
    this.peers.delete(peerId);
    return this.snapshot();
  }

  /** Drop peers silent past the TTL. Returns the snapshot; call on each presence tick. */
  prune(now: number): DirectoryPeer[] {
    for (const [id, p] of this.peers) {
      if (now - p.updatedAt > this.ttlMs) this.peers.delete(id);
    }
    return this.snapshot();
  }

  get(peerId: string): DirectoryPeer | undefined {
    return this.peers.get(peerId);
  }

  clear(): void {
    this.peers.clear();
  }

  /** Ordered: online first, then available operators, then by peerId. */
  snapshot(): DirectoryPeer[] {
    const availRank = (a?: AvailabilityStatus) => (a === 'active' ? 0 : a === 'busy' ? 1 : 2);
    return [...this.peers.values()].sort((a, b) => {
      if (a.online_status !== b.online_status) return a.online_status === 'online' ? -1 : 1;
      const ar = availRank(a.availability_status) - availRank(b.availability_status);
      if (ar !== 0) return ar;
      return a.peerId.localeCompare(b.peerId);
    });
  }

  get size(): number {
    return this.peers.size;
  }
}
